import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { useTrack } from "../lib/analytics";
import { articleBySlug } from "../lib/content";
import {
  type Quiz,
  type QuizOutcome,
  type QuizQuestion,
  quizBySlug,
  quizOutline,
  quizPath,
  scoreQuiz,
} from "../lib/quiz";
import { canonical } from "../lib/seo";
import { formatDate } from "./ArticleBody";
import { Contents, FaqList, renderBlock, ShortAnswer } from "./ProseBlocks";
import { StoreCta } from "./StoreCta";

/**
 * A quiz, rendered as a complete document that happens to be interactive.
 *
 * ── The rule this component exists to keep ───────────────────────────────────
 *
 * Every word is on the page before any JavaScript runs: the prose, all of the
 * questions, all of the options, every outcome in full. Nothing is fetched and
 * nothing is revealed by a script. A crawler that executes no JavaScript still
 * reads the whole thing, a person on a bad connection still gets it, and an
 * assistant summarising the page has the actual answers rather than a shell.
 *
 * That is not a nice-to-have. The usual way a quiz is built — a client-side
 * state machine that renders one question at a time — produces a URL whose
 * indexable content is a heading and a Start button, which is to say a page
 * with nothing on it. Interactivity here is an enhancement layered over a
 * document, in that order.
 *
 * ── Sharing is a hash, not a query string ────────────────────────────────────
 *
 * A shared result links to `#result-<id>`, an anchor on this same page. The
 * alternative — `?r=<id>` — would hand search engines one near-duplicate URL
 * per outcome, all of which would need a canonical tag and a `noindex` to undo.
 * A fragment is not a URL to a crawler at all, it costs nothing, and it works
 * with JavaScript switched off because the outcome is already in the document.
 */

function OptionButton({
  option,
  chosen,
  reveal,
  onChoose,
}: {
  option: { text: string; correct?: boolean };
  chosen: boolean;
  /** Knowledge quizzes only, and only once this question has been answered. */
  reveal: boolean;
  onChoose: () => void;
}) {
  const right = reveal && option.correct;
  const wrong = reveal && chosen && !option.correct;
  return (
    <button
      type="button"
      aria-pressed={chosen}
      onClick={onChoose}
      className={[
        "flex w-full items-start gap-3 rounded-[20px] border-[2.5px] px-4 py-3 text-left leading-snug transition-colors",
        right
          ? "border-ink bg-brand-wash text-ink"
          : wrong
            ? "border-ink bg-inset text-ink2 line-through"
            : chosen
              ? "border-ink bg-brand text-onbrand"
              : "border-hair bg-surface text-body hover:border-ink",
      ].join(" ")}
    >
      <span
        aria-hidden="true"
        className={[
          "mt-0.5 size-4 shrink-0 rounded-pill border-[2.5px]",
          chosen ? "border-ink bg-ink" : "border-hair",
        ].join(" ")}
      />
      <span>{option.text}</span>
    </button>
  );
}

function Question({
  quiz,
  question,
  index,
  chosen,
  onChoose,
}: {
  quiz: Quiz;
  question: QuizQuestion;
  index: number;
  chosen: number | undefined;
  onChoose: (option: number) => void;
}) {
  const answered = chosen !== undefined;
  const correct = question.options.find((o) => o.correct);
  return (
    <li className="brutal scroll-mt-24 p-5 sm:p-6" id={`q-${question.id}`}>
      {/* A real `<fieldset>` rather than a div with `role="group"`: the options
          are a set of related controls and the prompt is their legend, which is
          what a screen reader announces before the first option. A paragraph
          sitting above the group is not. */}
      <fieldset className="w-full">
        <legend className="w-full">
          <span className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
            question {index + 1}
          </span>
          <span className="mt-2 block font-display text-xl leading-snug font-bold text-ink">
            {question.prompt}
          </span>
        </legend>
        <div className="mt-4 flex flex-col gap-2.5">
          {question.options.map((option, i) => (
            <OptionButton
              key={option.text}
              option={option}
              chosen={chosen === i}
              reveal={quiz.kind === "knowledge" && answered}
              onChoose={() => onChoose(i)}
            />
          ))}
        </div>
      </fieldset>
      {/* Knowledge quizzes carry their reason on the page whether or not anyone
          answers — collapsed, so it is not a spoiler, and open the moment the
          question is answered. A `<details>` needs no JavaScript to work, which
          is the whole reason it is one. */}
      {quiz.kind === "knowledge" && question.because ? (
        <details
          open={answered}
          className="mt-4 border-t-[2.5px] border-hair pt-4"
        >
          <summary className="cursor-pointer list-none font-display text-sm font-bold text-scripture uppercase [&::-webkit-details-marker]:hidden">
            {answered ? "the answer" : "reveal the answer"}
          </summary>
          <p className="mt-2 leading-relaxed text-body">
            <strong className="text-ink">{correct?.text}</strong> —{" "}
            {question.because}
          </p>
        </details>
      ) : null}
    </li>
  );
}

function ResultCard({
  quiz,
  outcome,
  answered,
}: {
  quiz: Quiz;
  outcome: QuizOutcome;
  answered: number;
}) {
  const track = useTrack();
  const [copied, setCopied] = useState(false);
  const complete = answered === quiz.questions.length;
  const url = `${canonical(quizPath(quiz.slug))}#result-${outcome.id}`;
  const text = `${quiz.h1} — I got “${outcome.name}”.`;

  async function share() {
    track("web_quiz_share", { quiz: quiz.slug, outcome: outcome.id });
    // `navigator.share` on a phone is the only route that reaches the app the
    // person actually wants to send this in. Everywhere else, the clipboard —
    // a "share to X" button on a desktop is a link to a login screen.
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: quiz.h1, text, url });
        return;
      } catch {
        // Cancelled, or refused by the browser. Fall through to the clipboard.
      }
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <aside
      aria-live="polite"
      className="brutal mt-8 bg-brand-wash p-6 sm:p-7"
      id="your-result"
    >
      <p className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
        {complete
          ? "your result"
          : `your result so far · ${answered} of ${quiz.questions.length} answered`}
      </p>
      <p className="mt-2 font-display text-[clamp(1.4rem,4vw,1.9rem)] leading-tight font-bold text-ink text-balance">
        {outcome.name}
      </p>
      <p className="mt-3 leading-relaxed text-body">{outcome.summary}</p>
      <div className="mt-5 flex flex-wrap gap-3">
        <a
          href={`#result-${outcome.id}`}
          className="rim inline-flex h-[46px] items-center justify-center bg-brand px-5 font-display text-[15px] font-bold text-onbrand"
        >
          read the full result
        </a>
        <button
          type="button"
          onClick={share}
          className="rim inline-flex h-[46px] items-center justify-center bg-surface px-5 font-display text-[15px] font-bold text-ink"
        >
          {copied ? "link copied" : "share this result"}
        </button>
      </div>
    </aside>
  );
}

function Outcome({
  quiz,
  outcome,
  current,
}: {
  quiz: Quiz;
  outcome: QuizOutcome;
  current: boolean;
}) {
  return (
    <article
      id={`result-${outcome.id}`}
      className={[
        "mt-6 scroll-mt-24 rounded-card border-[2.5px] p-6 sm:p-7",
        current
          ? "border-ink bg-surface shadow-[5px_5px_0_0_var(--color-ink)]"
          : "border-hair bg-surface",
      ].join(" ")}
    >
      {outcome.min !== undefined ? (
        <p className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
          {outcome.min === 0
            ? `0 – ${bandTop(quiz, outcome)} correct`
            : `${outcome.min} – ${bandTop(quiz, outcome)} correct`}
        </p>
      ) : null}
      <h3 className="mt-1 font-display text-2xl leading-tight font-bold text-ink">
        {outcome.name}
      </h3>
      <p className="mt-3 text-lg leading-relaxed text-ink2">
        {outcome.summary}
      </p>
      {outcome.body.map((paragraph) => (
        <p
          key={paragraph.slice(0, 40)}
          className="mt-5 leading-[1.75] text-body"
        >
          {paragraph}
        </p>
      ))}
      {renderBlock({ type: "verse", verse: outcome.verse }, 0)}
      <h4 className="mt-7 font-display text-lg font-bold text-ink">
        Three things to do
      </h4>
      <ol className="mt-4 flex flex-col gap-4">
        {outcome.practice.map((item, n) => (
          <li key={item} className="flex gap-3.5 leading-[1.75] text-body">
            <span
              aria-hidden="true"
              className="inline-flex size-7 shrink-0 items-center justify-center rounded-pill border-[2.5px] border-ink bg-surface font-display text-sm font-bold text-ink"
            >
              {n + 1}
            </span>
            <span className="pt-0.5">{item}</span>
          </li>
        ))}
      </ol>
    </article>
  );
}

/** The top of a knowledge band — one below the next band's floor. */
function bandTop(quiz: Quiz, outcome: QuizOutcome): number {
  const next = quiz.outcomes
    .map((o) => o.min ?? 0)
    .filter((min) => min > (outcome.min ?? 0))
    .sort((a, b) => a - b)
    .at(0);
  return next === undefined ? quiz.questions.length : next - 1;
}

function RelatedQuizzes({ slugs }: { slugs: string[] }) {
  const quizzes = slugs.map(quizBySlug).filter((q) => q !== undefined);
  if (quizzes.length === 0) return null;
  return (
    <section className="mt-14 border-t-[2.5px] border-ink pt-10">
      <h2 className="font-display text-2xl font-bold text-ink">Take another</h2>
      <ul className="mt-5 grid gap-4 sm:grid-cols-2">
        {quizzes.map((q) => (
          <li key={q.slug}>
            <Link
              to="/quiz/$slug"
              params={{ slug: q.slug }}
              className="brutal flex h-full flex-col gap-2 p-5 transition-transform hover:-translate-y-0.5"
            >
              <span className="font-display text-lg leading-snug font-bold text-ink">
                {q.h1}
              </span>
              <span className="text-sm leading-relaxed text-ink2">
                {q.description}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function RelatedArticles({ slugs }: { slugs: string[] }) {
  const articles = slugs.map(articleBySlug).filter((a) => a !== undefined);
  if (articles.length === 0) return null;
  return (
    <section className="mt-12">
      <h2 className="font-display text-2xl font-bold text-ink">Read next</h2>
      <ul className="mt-5 grid gap-4 sm:grid-cols-2">
        {articles.map((a) => (
          <li key={a.slug}>
            <Link
              to="/blog/$slug"
              params={{ slug: a.slug }}
              className="brutal flex h-full flex-col gap-2 p-5 transition-transform hover:-translate-y-0.5"
            >
              <span className="font-display text-lg leading-snug font-bold text-ink">
                {a.h1}
              </span>
              <span className="text-sm leading-relaxed text-ink2">
                {a.description}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function QuizRunner({ quiz }: { quiz: Quiz }) {
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const track = useTrack();
  const reported = useRef(false);

  const answered = Object.keys(answers).length;
  const outcome = scoreQuiz(quiz, answers);
  const complete = answered === quiz.questions.length;

  useEffect(() => {
    // Once per visit, when the last question lands. The event is what separates
    // "the page is read" from "the page is finished", and those are different
    // problems with different fixes.
    if (!complete || reported.current || !outcome) return;
    reported.current = true;
    track("web_quiz_completed", { quiz: quiz.slug, outcome: outcome.id });
  }, [complete, outcome, quiz.slug, track]);

  return (
    <article className="mx-auto w-full max-w-[68ch]">
      <p className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
        {formatDate(quiz.published)}
        {quiz.updated ? ` · updated ${formatDate(quiz.updated)}` : ""} ·{" "}
        {quiz.questions.length} questions
      </p>
      <h1 className="mt-3 font-display text-[clamp(1.9rem,6vw,2.9rem)] leading-[1.15] font-bold tracking-tight text-ink text-balance">
        {quiz.h1}
      </h1>
      <p className="mt-4 text-[clamp(1.05rem,2.4vw,1.2rem)] leading-relaxed text-ink2">
        {quiz.standfirst}
      </p>

      <ShortAnswer takeaways={quiz.takeaways} />
      <Contents headings={quizOutline(quiz)} />

      {quiz.intro.map(renderBlock)}

      <section className="mt-12">
        <h2
          id="the-questions"
          className="scroll-mt-24 font-display text-[clamp(1.4rem,3.5vw,1.9rem)] leading-tight font-bold text-ink"
        >
          The questions
        </h2>
        <ol className="mt-6 flex flex-col gap-5">
          {quiz.questions.map((question, i) => (
            <Question
              key={question.id}
              quiz={quiz}
              question={question}
              index={i}
              chosen={answers[question.id]}
              onChoose={(option) =>
                setAnswers((prev) => ({ ...prev, [question.id]: option }))
              }
            />
          ))}
        </ol>
        {outcome ? (
          <ResultCard quiz={quiz} outcome={outcome} answered={answered} />
        ) : (
          <p className="mt-8 leading-relaxed text-ink2">
            Answer any question and your result appears here — you do not have
            to finish to see what your answers point at.
          </p>
        )}
        {answered > 0 ? (
          <button
            type="button"
            onClick={() => {
              setAnswers({});
              reported.current = false;
            }}
            className="mt-4 text-sm text-ink2 underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
          >
            start again
          </button>
        ) : null}
      </section>

      <section className="mt-14">
        <h2
          id="every-result"
          className="scroll-mt-24 font-display text-[clamp(1.4rem,3.5vw,1.9rem)] leading-tight font-bold text-ink"
        >
          {quiz.kind === "knowledge"
            ? "What the scores mean"
            : "Every result, in full"}
        </h2>
        <p className="mt-4 leading-[1.75] text-body">
          {quiz.kind === "knowledge"
            ? "All four bands are below, because a score is only interesting if you can see what the others say."
            : "All of them are here rather than hidden behind the one you were given — the most useful question after a result like this is usually which of the others you have needed and not been."}
        </p>
        {quiz.outcomes.map((o) => (
          <Outcome
            key={o.id}
            quiz={quiz}
            outcome={o}
            current={outcome?.id === o.id}
          />
        ))}
      </section>

      {quiz.after.map(renderBlock)}

      {quiz.faq ? <FaqList items={quiz.faq} /> : null}

      <StoreCta from={`quiz:${quiz.slug}`} />

      <RelatedQuizzes slugs={quiz.relatedQuizzes ?? []} />
      <RelatedArticles slugs={quiz.related} />
    </article>
  );
}
